import { GameState, PieceType } from '../../types';
import type { ZodiacEngine } from '../../zodiacEngine';
import { IAIPlayer, AIMoveResult, AIStats, AIPlayerType } from './IAIPlayer';
import { observeForBlind, kingWorlds } from '../AstraBlindAI/Observation';
import { AstraRules, moveKey } from '../AstraAI/AstraRules';
import { MonteCarloAI } from '../MonteCarloAI';
import { MinimaxAI } from '../MinimaxAI';
import { CreatureAI } from '../CreatureAI';

/** Uniform king hypotheses, using the selected algorithm's votes, not comparable scores.
 * No live engine reaches a search. Hypothesis-specific PVs are intentionally omitted.
 */
export class BlindAI implements IAIPlayer {
  readonly name: string;
  readonly displayName: string;
  readonly description = 'Searches plausible enemy king identities without seeing the real king';
  private stats: AIStats = { movesCalculated: 0, averageThinkingTime: 0 };
  constructor(private type: AIPlayerType) {
    this.name = `${type}-blind`;
    this.displayName = `${type} Blind`;
  }
  selectKing(kings: PieceType[]): PieceType { return kings[Math.floor(Math.random() * kings.length)]; }
  private create(state: GameState): IAIPlayer {
    // Legacy constructors accept ZodiacEngine, but these algorithms only read
    // getGameState (Creature); supplying a facade prevents live-state access.
    const engine = { getGameState: () => state } as ZodiacEngine;
    switch (this.type) {
      case AIPlayerType.MCTS: return new MonteCarloAI(engine);
      case AIPlayerType.MINIMAX: return new MinimaxAI(engine);
      case AIPlayerType.CREATURE: return new CreatureAI(engine);
      default: throw new Error(`Unsupported blind AI: ${this.type}`);
    }
  }
  async selectMove(input: GameState, budgetMs: number): Promise<AIMoveResult> {
    const started = Date.now();
    const observation = observeForBlind(input);
    const worlds = kingWorlds(observation);
    const legal = new AstraRules().legalMoves(observation);
    if (!legal.length) throw new Error('Blind AI has no legal move');
    const votes = new Map<string, number>();
    const slice = Math.max(1, budgetMs * 0.9 / Math.max(1, worlds.length));
    const deadline = started + Math.max(1, budgetMs * 0.9);
    let completed = 0;
    for (const world of worlds) {
      const remaining = deadline - Date.now();
      if (remaining <= 0) break;
      let result: AIMoveResult;
      try {
        result = await this.create(world.state).selectMove(world.state, Math.min(slice, remaining));
      } catch (error) {
        // Legacy searches may not finish even their first iteration in a tiny
        // time slice. Use a public legal fallback rather than failing the turn.
        if (error instanceof Error && /AI failed to find a valid move/.test(error.message)) break;
        throw error;
      }
      completed++;
      const key = moveKey(result);
      if (legal.some(m => moveKey(m) === key)) votes.set(key, (votes.get(key) || 0) + world.probability);
    }
    // Do not let an incomplete subset of king hypotheses bias the decision.
    if (completed !== worlds.length) votes.clear();
    const ranked = [...legal].sort((a, b) =>
      (votes.get(moveKey(b)) || 0) - (votes.get(moveKey(a)) || 0) || moveKey(a).localeCompare(moveKey(b)));
    const chosen = ranked[0];
    // Reconnect executor references only after the decision is final.
    const pieces = new Map(input.board.flatMap(r => r.flatMap(s => s.Pieces.map(p => [p.id, p] as const))));
    const move = { ...chosen.move,
      toPiece: chosen.move.toPiece ? pieces.get(chosen.move.toPiece.id) || null : null,
      sideEffects: chosen.move.sideEffects.map(e => ({ ...e,
        piecesToEffect: e.piecesToEffect.map(p => pieces.get(p.id)!).filter(Boolean) })) };
    const thinkingTime = Date.now() - started;
    this.stats.averageThinkingTime = (this.stats.averageThinkingTime * this.stats.movesCalculated + thinkingTime) / ++this.stats.movesCalculated;
    return { fromPosition: chosen.fromPosition, move, thinkingTime,
      confidence: votes.get(moveKey(chosen)) || 0,
      reasoning: completed === worlds.length ? `${this.type} blind: consensus across ${worlds.length} possible enemy kings` : `${this.type} blind: legal fallback (thinking budget exhausted)`,
      evaluationBreakdown: { blind: true, completedHypotheses: completed, kingBeliefs: worlds.map(w => ({ id: w.candidateId, probability: w.probability })),
        method: 'Uniform hypothesis vote; each hypothesis uses the selected algorithm and an equal share of the time budget.' } };
  }
  getStats(): AIStats { return { ...this.stats }; }
  reset(): void { this.stats = { movesCalculated: 0, averageThinkingTime: 0 }; }
}
